import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Checkbox,
  Avatar,
  useMediaQuery,
  Tooltip,
  Modal,
} from "@mui/material";
import MainLayout from "../../components/MainLayout";
import TotalCounter from "../../components/TotalCounter";
import MakeAccountCard from "../../components/MakeAccountCard";
import { SummaryDataGrid } from "../../components/AppDataGrid";
import ActorDetails from "../../components/Actor/ActorDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteArtistProfiles,
  fetchArtistDataById,
  getArtistList,
  setArtist,
} from "../../actions/artist";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Verified,
} from "@mui/icons-material";
import { StorageConstants } from "../../storage/storageConstants";
import {
  deepCopy,
  hideLoadingOverlay,
  showLoadingOverlay,
} from "../../../utils";
import { useMessage } from "../../../base/MessageProvider";
import ActorProfileForm from "../../components/Actor/ActorProfileForm";
import SlideStack from "../../components/SlideStack";
import {
  getGridStringOperators,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";
import { getProfileCategoryList } from "../../actions/profileCategory";
import {
  getArtistExperienceCategoryList,
  getExperienceTypeList,
  getLocationList,
} from "../../actions/general";
import OAuth from "../../components/OAuth";
import useDebounce from "../../../base/useDebounce";
import { kycStatusOptions } from "../../../constants";

import NotificationsModal from "../../components/NotificationsModal";
import SendNotificationCard from "../../components/SendNotificationCard";
import { sendNotification } from "../../actions/notification";

/**
 * @author Sarthak Sharma.
 * @since 27-12-2023.
 * @description Actor management page.
 * @returns React.ReactElement
 */

const ActorManagement = () => {
  const storedPage = localStorage.getItem(StorageConstants.ARTIST_PAGE);
  const storedPageSize = localStorage.getItem(StorageConstants.ARTIST_PER_PAGE);

  const min900px = useMediaQuery("(min-width: 900px)");

  const dispatch = useDispatch();
  const alert = useMessage();
  const loading = useSelector((state) => state.artistReducer?.loading);
  const artistList =
    useSelector((state) => state.artistReducer?.artistList) ?? [];
  const artistListMetaData = useSelector(
    (state) => state.artistReducer?.artistListMetaData
  );
  const selectedArtist = useSelector(
    (state) => state.artistReducer?.selectedArtist
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filterItem, setFilterItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [checkedId, setCheckedId] = useState([]);
  const [modalOpen, setModalOpen] = useState({ open: false, type: undefined });
  const [rowsState, setRowsState] = useState({
    page: storedPage ? +storedPage : 0,
    pageSize: storedPageSize ? +storedPageSize : 50,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 800);
  const [notificationMmodalOpen, setNotificationMmodalOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  useEffect(() => {
    const init = async () => {
      dispatch(getProfileCategoryList({ categoryType: "Artist" }));
      dispatch(getLocationList());
      dispatch(getArtistExperienceCategoryList({ categoryType: "Artist" }));
      dispatch(getExperienceTypeList());
    };
    init();
  }, []);

  const handleChange = (id, event) => {
    if (!event.target.checked) setCheckedId(checkedId.filter((a) => a !== id));
    else setCheckedId([...checkedId, id]);
  };

  const onPaginationModelChange = (model) => {
    setRowsState(model);
    localStorage.setItem(
      StorageConstants.ARTIST_PAGE,
      JSON.stringify(model.page)
    );
    localStorage.setItem(
      StorageConstants.ARTIST_PER_PAGE,
      JSON.stringify(model.pageSize)
    );
  };

  const onFilterModelChange = (model) => {
    const filterModel = deepCopy(model);
    console.log(filterModel.items);

    if (filterModel.items.length && !filterModel.items[0].value) {
      setFilterItem(null);
      return;
    }
    setFilterItem(filterModel.items[0]);
  };

  const onRowClick = async (params) => {
    showLoadingOverlay("Fetching data");
    await dispatch(
      fetchArtistDataById(
        { profileId: params?.row?.userId },
        () => {
          setModalOpen({ open: true, type: "EditForm" });
        },
        (error) => {
          alert.showError(error?.message ?? "Something went wrong");
        }
      )
    );
    hideLoadingOverlay();
  };

  const handleDownload = async () => {
    console.log("Actor download!!");
  };

  const onClickAddButton = async () => {
    await dispatch(setArtist(null));
    setModalOpen({ open: true, type: "AddForm" });
  };

  const onClickNotification = async () => {
    setNotificationMmodalOpen({ open: true });
  };

  const fetchData = async () => {
    const kycStatus =
      filterItem?.field === "kycStatus" ? filterItem?.value : undefined;

    const searchedKeyword = debouncedSearchTerm
      ? debouncedSearchTerm?.trim()
      : filterItem?.field === "kycStatus"
      ? undefined
      : filterItem?.value;

    setRows([]);
    dispatch(
      getArtistList({
        page: +rowsState.page + 1,
        perPage: rowsState.pageSize,
        searchedKeyword: searchedKeyword,
        kycStatus: kycStatus,
      })
    );
  };

  useEffect(() => {
    if (artistList) {
      setRows(
        artistList.map((item, index) => ({
          ...item,
          id: index.toString(),
          code: `#${item.userId?.substr(item.userId?.length - 5)}`,
          fullName: `${item.firstName} ${item?.lastName ?? ""}`,
          profilePic: item?.userProfile?.profilePic?.url,
          profileCategory:
            item?.userProfile?.profileCategories
              ?.map?.((item) => item.name)
              ?.join?.(", ") ?? "",
          kycStatus: item?.kycStatus,
        }))
      );
    }
  }, [artistList]);

  useEffect(() => {
    fetchData();
  }, [rowsState, debouncedSearchTerm, filterItem]);

  const columns = useMemo(
    () => [
      {
        flex: 1,
        field: "fullName",
        headerName: "Name",
        description: "This column has a value getter and is not sortable.",
        minWidth: 200,
        renderCell: (params) => (
          <div className="flex gap-3 items-center text-[12px] font-['Poppins-Regular'] text-[#202223]">
            <OAuth
              // altSrc={params.row.profilePic}
              component={
                <Avatar
                  sx={{ width: 30, height: 30 }}
                  src={params.row.profilePic}
                />
              }
            />
            <div className="flex gap-1 items-center">
              {params.value}
              {params.row?.verified && (
                <Verified className="text-[#5D43FF] text-base" />
              )}
            </div>
          </div>
        ),
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        field: "code",
        headerName: "Code",
        valueGetter: (params) => params.value ?? "-",
        width: 90,
        filterable: false,
      },
      {
        flex: 1,
        field: "email",
        headerName: "Email",
        valueGetter: (params) => params.value ?? "-",
        minWidth: 160,
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        // flex: 1,
        field: "mobileNo",
        headerName: "Mobile No.",
        valueGetter: (params) => params.value ?? "-",
        width: 130,
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        flex: 1,
        field: "profileCategory",
        headerName: "Category",
        valueGetter: (params) => params.value ?? "-",
        renderCell: (params) => {
          return (
            <Tooltip
              className="text-[11px] text-black font-medium"
              title={params?.value ?? "-"}
            >
              {params?.value ?? "-"}
            </Tooltip>
          );
        },
        minWidth: 130,
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        flex: 1,
        field: "kycStatus",
        headerName: "KYC",
        valueGetter: (params) =>
          params.value ? params.value : "KYC NOT GENERATED",
        renderCell: (params) => (
          <Tooltip
            className="text-[11px] text-black font-medium"
            title={params?.value}
          >
            {params?.value}
          </Tooltip>
        ),
        minWidth: 100,
        type: "singleSelect",
        valueOptions: kycStatusOptions,
        filterOperators: getGridSingleSelectOperators().filter(
          (a) => a.value === "is"
        ),
      },
      {
        field: "userId",
        headerName: "",
        type: "actions",
        renderCell: (params) => (
          <Checkbox
            checked={checkedId?.includes(params.value)}
            onChange={handleChange?.bind(this, params.value)}
            icon={<RadioButtonUnchecked />}
            color="secondary"
            checkedIcon={<CheckCircle />}
          />
        ),
        width: 60,
        sortable: false,
        filter: false,
      },
    ],
    [checkedId]
  );

  const handleClose = () => {
    dispatch(setArtist(null));
    setModalOpen({ open: false, type: false });
  };

  const onSuccessCallBack = (res) => {
    alert.showSuccess("Successfully sent!!");
    setNotificationMmodalOpen(false);
  };

  const onerrorCallBack = (error) => {
    const errorMessage = error?.message;
    alert.showError(errorMessage, { timeout: 10000 });
  };

  const submitNotification = (data) => {
    const includesProfileCategory = data?.profileCategoryId?.join(",") ?? "";
    const cleanedDescription = data?.description
      ?.replace(/^<p>/i, "") // Remove opening <p> tag at the start
      ?.replace(/<\/p>$/i, ""); // Remove closing </p> tag at the end
    // ?.replace(/<\/?(p|strong|em|u|s)>/gi, ""); // Remove <strong>, <em>, <u>, <s> tags

    const request = {
      title: data?.notificationTitle,
      body: cleanedDescription,
    };

    console.log(
      "Print the value of cleanedDescription >>> ",
      cleanedDescription
    );

    const params = {
      toAllArtist: true,
      minAge: data?.ageRange?.[0] ?? 0,
      maxAge: data?.ageRange?.[1] ?? 70,
      gender: data?.gender,
      includesProfileCategory,
    };

    dispatch(
      sendNotification(request, params, onSuccessCallBack, onerrorCallBack)
    );
  };

  const onUpdateData = () => {
    handleClose();
    fetchData();
  };

  const handleDelete = async () => {
    showLoadingOverlay();
    dispatch(
      deleteArtistProfiles(
        { includesProfileId: checkedId?.join(",") },
        (res) => {
          onUpdateData();
          setCheckedId([]);
          alert.showSuccess(res?.message ?? "Successfully deleted");
        },
        (error) => {
          alert.showError(error?.message ?? "Something went wrong");
        }
      )
    );
    hideLoadingOverlay();
  };

  const onPressDeleteAll = () => {
    if (!checkedId?.length) {
      alert.showError("Please select atleast one profile!!");
      return;
    }
    alert.showConfirmation({
      message: "Are you sure you want to delete?",
      button1Title: "Cancel",
      button2Title: "Delete",
      onPressBtn1: () => {},
      onPressBtn2: handleDelete,
    });
  };

  const SideBarData =
    modalOpen?.type === "EditForm"
      ? [
          {
            name: "ActorDetails",
            component: ActorDetails,
            props: { onRefresh: fetchData, onClose: onUpdateData },
          },
          {
            name: "ActorProfileForm",
            component: ActorProfileForm,
            props: { onRefresh: fetchData, onClose: onUpdateData },
          },
        ]
      : [
          {
            name: "ActorProfileForm",
            component: ActorProfileForm,
            props: {
              onRefresh: fetchData,
              onClose: onUpdateData,
              addNew: true,
            },
          },
        ];

  return (
    <MainLayout
      open={modalOpen?.open}
      onClose={handleClose}
      FormComponent={
        <SlideStack
          key={selectedArtist?.userId ?? "key"}
          open={modalOpen?.open}
          components={SideBarData}
        />
      }
    >
      <Box className="relative w-full flex flex-col gap-8 items-center p-4 py-12">
        <Box className="w-full flex flex-wrap bg-white rounded-[12px] border-[2px] border-[#E8E8E9] ">
          <TotalCounter
            title="TOTAL ACCOUNTS"
            counts={artistListMetaData?.total_user ?? 0}
            bottomLeftComponent={
              <div className="flex gap-3 items-center">
                <div className="bg-sky-50 rounded-[3px] flex gap-2 items-center px-2 w-fit">
                  <div className="w-[7px] h-[7px] bg-teal-500 rounded-full" />
                  <div className="text-black text-sm font-['Poppins-Regular'] leading-[30px]">
                    LIVE
                  </div>
                </div>
                <div className="text-zinc-500 text-sm font-['Poppins-Regular'] leading-[30px]">
                  120 People
                </div>
              </div>
            }
            linkText={"See Live View"}
          />
          <Divider
            orientation={min900px ? "vertical" : "horizontal"}
            style={{ width: min900px ? "fit-content" : "100%" }}
            flexItem
          />
          <TotalCounter
            title="VERIFIED ACCOUNTS"
            counts={artistListMetaData?.total_verified_user ?? 0}
            bottomLeftComponent={
              <div className="text-zinc-500 text-sm font-['Poppins-Regular'] leading-[30px]">
                Premium user report
              </div>
            }
            linkText={"View Report"}
            onClickLink={() => {}}
          />
        </Box>
        <Box className="w-full flex bg-[#E8F5E9] rounded-[12px] border-[2px] border-[#C8E6C9] ">
          <SendNotificationCard
            onClickAdd={onClickNotification}
            title="Send Notifications"
            subTitle="Send customized Notifications to all Artists..."
            buttonText="Send Notifications"
          />
        </Box>
        <Box className="w-full flex bg-[#E8F5E9] rounded-[12px] border-[2px] border-[#C8E6C9] ">
          <MakeAccountCard
            onClickAdd={onClickAddButton}
            title="Make an Actor Profile"
            subTitle="Make profiles from backend and release it on the app."
            buttonText="Create Actor Profile"
          />
        </Box>

        <Box className="w-full flex bg-white rounded-[12px] border-[2px] border-[#E8E8E9] ">
          <SummaryDataGrid
            title="List of all Actor profiles"
            subTitle="Review, Report or Delete the profiles at your wish."
            paginationModel={rowsState}
            onPaginationModelChange={onPaginationModelChange}
            onFilterModelChange={onFilterModelChange}
            rowCount={+artistListMetaData?.totalPaginatedUser || 0}
            columns={columns}
            rows={rows}
            getRowClassName={(params) =>
              params.row?.userId === selectedArtist?.userId ? "bg-gray-200" : ""
            }
            loading={loading}
            onRowClick={onRowClick}
            onPressDelete={onPressDeleteAll}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onClick={handleDownload}
          />
          {/* <DevDataGrid /> */}
        </Box>
        <Modal
          open={notificationMmodalOpen}
          onClose={setNotificationMmodalOpen?.bind(this, false)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: isMobile ? "90vw" : isTablet ? "50vw" : 450,
              height: isMobile ? "60vh" : isTablet ? "80vh" : "75%",
              boxShadow: 24,
              p: isMobile ? 1.5 : isTablet ? 2 : 3,
              textAlign: "center",
              borderRadius: "8px",
              backgroundColor: "#fff",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              overflow: "hidden", // Prevents scrolling issues
            }}
          >
            <NotificationsModal
              setAiModalOpen={setNotificationMmodalOpen}
              sendFor={"Actor"}
              onSubmit={submitNotification}
              onClose={setNotificationMmodalOpen?.bind(this, false)}
            />
          </Box>
        </Modal>
      </Box>
    </MainLayout>
  );
};

export default React.memo(ActorManagement, () => true);
